import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";

// Map.js
import React, { useEffect, useState, useRef } from "react";
import styles from "./Map.module.scss";

import FilterControl from "../Filter/FilterControl";

import { useGeoJsonData } from "../../GeoJsonDataContext";
import { buildFilterOptions } from "../../filterOptions";

import { INTRO_TEXT, MAPBOX_ACCESS_TOKEN, MEDIA_BASE_URL } from "../../settings";

// Device detection
import { isMobile } from "react-device-detect";

const MEDIA_URL = MEDIA_BASE_URL;

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

// Single source of truth for schoollevel -> marker icon, shared by getIcon()
// (used for the click popup) and the "messages" layer's icon-image match
// expression below, so the two can't silently drift apart.
const DEFAULT_ICON_NAME = "amethyst";
const SCHOOL_LEVEL_ICON_NAMES = {
  "Early Childhood": "emerald",
  "Primary School": "ruby",
  "Secondary School": "diamond",
  "special-minister": DEFAULT_ICON_NAME,
  "special-ceo": "yellowDiamond",
};

// The schoollevel values of the two always-appended SPECIAL_FEATURES. Draw
// order and click priority both key off this list, so keep it the only copy.
const SPECIAL_SCHOOL_LEVELS = ["special-ceo", "special-minister"];

/**
 * Pick which feature a click on the "messages" layer selected.
 *
 * Mapbox hands the handler every feature under the cursor, in an order that
 * ignores symbol-sort-key -- so when zoomed out, where gems overlap heavily,
 * a click aimed at a special gem drawn on top could still select a submitted
 * gem underneath it. Prefer a special gem whenever one is under the pointer,
 * and when both special gems are (they're only ~45km apart, so they overlap
 * at low zoom) take whichever one the click actually landed nearer to.
 *
 * `point` is the click position in screen pixels and `project` turns a
 * feature's lng/lat into the same space; both are optional so the ordering
 * rule still holds if a caller has neither.
 */
export const pickClickedFeature = (features, point, project) => {
  const specials = features.filter((feature) =>
    SPECIAL_SCHOOL_LEVELS.includes(feature.properties.schoollevel)
  );
  if (specials.length === 0) return features[0];
  if (specials.length === 1 || !point || typeof project !== "function") {
    return specials[0];
  }

  const distanceToClick = (feature) => {
    const coordinates = feature.geometry && feature.geometry.coordinates;
    if (!coordinates) return Infinity;
    const projected = project(coordinates);
    return (
      (projected.x - point.x) * (projected.x - point.x) +
      (projected.y - point.y) * (projected.y - point.y)
    );
  };

  return specials.reduce((nearest, feature) =>
    distanceToClick(feature) < distanceToClick(nearest) ? feature : nearest
  );
};

export function getIcon(schoollevel) {
  const iconName = SCHOOL_LEVEL_ICON_NAMES[schoollevel] || DEFAULT_ICON_NAME;
  return `${MEDIA_URL}/img/${iconName}.png`;
}

/**
 * The interactive Mapbox GL map view: renders one marker per geoJsonData
 * feature (icon keyed by schoollevel, see SCHOOL_LEVEL_ICON_NAMES above),
 * a school/postcode filter control, a retractable geocoder search, and a
 * click-to-view-message popup rendered into infoContainer. Owns the
 * Mapbox GL instance directly (not via react-map-gl). Takes no props;
 * reads geoJsonData from context. No automated test coverage — see
 * CLAUDE.md's Testing section for why.
 */
const Map = () => {
  const { geoJsonData, loadError } = useGeoJsonData();

  const mapContainer = useRef(null);
  const infoContainer = useRef(null);

  const [map, setMap] = useState(null);
  const [styleLoading, setStyleLoading] = useState(true);
  const [iconsLoading, setIconsLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);

  const introduction = `
    <div class="${styles.icon} ${styles.group}">
      <img id=${styles.gemRuby} src="${MEDIA_URL}/img/WTD2023-Ruby.svg" alt="" role="presentation" />
      <img id=${styles.gemEmerald} src="${MEDIA_URL}/img/WTD2023-Emerald.svg" alt="" role="presentation" />
      <img id=${styles.gemDiamond} src="${MEDIA_URL}/img/WTD2023-Diamond.svg" alt="" role="presentation" />
    </div>
    <div class="${styles.messageContainer} ${styles.intro}">
      <p class=${styles.message}>${INTRO_TEXT}</p>
      <span class=${styles.sender}>NSW Education Standards Authority</span>
    </div>
  `;

  // Expands a [minLon, minLat, maxLon, maxLat] bbox by `buffer` degrees in
  // every direction, used to set maxBounds so the map can't be panned too
  // far past the edge of NSW. Mobile gets extra bottom padding since the
  // search/filter UI sits lower in the viewport there.
  function bufferBbox(bbox, buffer) {
    const [minLon, minLat, maxLon, maxLat] = bbox;

    return [
      minLon - buffer,
      minLat - (isMobile ? buffer + 2.5 : buffer),
      maxLon + buffer,
      maxLat + buffer,
    ];
  }

  useEffect(() => {
    // This effect will be called once when the component mounts and when geojsonData updates
    const initializeMap = ({ setMap, mapContainer }) => {
      // Bounding box for New South Wales (NSW)
      const nswBbox = [140.9993, -37.5051, 153.6388, -28.157];
      const bufferredBBox = bufferBbox(nswBbox, isMobile ? 2.2 : 5);
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/smallmultiples/clme8z9pl00n701pv7b87fzfw",
        center: isMobile
          ? [147.31905, -34.47493703000571]
          : [147.31905, -33.083640948961005], // Approximate center of NSW
        // zoom: 3, // Adjust this value as needed to show the entire state
        maxBounds: bufferredBBox,
      });

      // Animated icon
      const size = 200;
      // This implements `StyleImageInterface`
      // to draw a pulsing dot icon on the map.
      const animatedIcon = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),

        // When the layer is added to the map,
        // get the rendering context for the map canvas.
        onAdd: function () {
          const canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
        },

        // Call once before every frame where the icon will be used.
        render: function () {
          const duration = 1000;
          const t = (performance.now() % duration) / duration;

          const radius = (size / 2) * 0.3;
          const outerRadius = (size / 2) * 0.7 * t + radius;
          const context = this.context;

          // Draw the outer circle.
          context.clearRect(0, 0, this.width, this.height);
          context.beginPath();
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2
          );
          context.fillStyle = `rgba(255, 243, 209, ${0.8 - t})`;
          context.fill();

          // Update this image's data with data from the canvas.
          this.data = context.getImageData(0, 0, this.width, this.height).data;

          // Continuously repaint the map, resulting
          // in the smooth animation of the dot.
          map.triggerRepaint();

          // Return `true` to let the map know that the image was updated.
          return true;
        },
      };

      map.on("style.load", function () {
        setStyleLoading(false);
      });

      map.on("load", () => {
        if (geoJsonData) {
          // debugger
          map.addImage("animatedDot", animatedIcon, { pixelRatio: 2 });

          // Add an event listener to change the cursor when hovering over symbols
          map.on("mouseenter", "symbols", function () {
            map.getCanvas().style.cursor = "pointer";
          });

          map.on("mouseleave", "symbols", function () {
            map.getCanvas().style.cursor = "";
          });

          // Initialize and add the dropdown control
          // const dropdownControl = new DropdownControl();
          // map.addControl(dropdownControl, 'top-right'); // You can choose the position

          const options = buildFilterOptions(geoJsonData.features);

          const handleInputChange = (selectedOption) => {
            if (!selectedOption || selectedOption.value === "all") {
              map.setFilter("messages", null); // If input is cleared, show all markers
            } else {
              map.setFilter("messages", [
                "any",
                ["==", ["get", "school"], selectedOption.value],
                ["==", ["get", "postcode"], selectedOption.value],
              ]);
              const visibleMessages = geoJsonData.features
                .filter(
                  (m) =>
                    m.properties.school === selectedOption.value ||
                    m.properties.postcode === selectedOption.value
                )
                .map((m) => [m.properties.longitude, m.properties.latitude]);
              // // Find bounds of filtered coordinates
              const bounds = new mapboxgl.LngLatBounds();
              if (visibleMessages.length > 0) {
                // debugger;
                visibleMessages.forEach((coord) => {
                  if (
                    Array.isArray(coord) &&
                    coord.length === 2 &&
                    typeof coord[0] === "number" &&
                    typeof coord[1] === "number"
                  ) {
                    // bounds.extend(coord);
                    bounds.extend({ lat: coord[1], lng: coord[0] });
                  }
                });
              }
              // Zoom map to fit bounds
              if (bounds && bounds._ne && bounds._sw) {
                map.fitBounds(bounds, { padding: 30, maxZoom: 11 });
              }
            }
          };
          const filterControl = new FilterControl(options, handleInputChange);
          map.addControl(filterControl, "top-left");

          var RetractableGeocoderControl = {
            onAdd: function (map) {
              this.map = map;
              this.container = document.createElement("div");
              this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

              var button = document.createElement("button");
              button.className = `mapboxgl-ctrl-icon mapboxgl-ctrl-search ${styles.searchButton}`;
              button.innerHTML =
                '<svg class="mapboxgl-ctrl-geocoder--icon mapboxgl-ctrl-geocoder--icon-search" width="20px" height="20px"><path d="M7.4 2.5c-2.7 0-4.9 2.2-4.9 4.9s2.2 4.9 4.9 4.9c1 0 1.8-.2 2.5-.8l3.7 3.7c.2.2.4.3.8.3.7 0 1.1-.4 1.1-1.1 0-.3-.1-.5-.3-.8L11.4 10c.4-.8.8-1.6.8-2.5.1-2.8-2.1-5-4.8-5zm0 1.6c1.8 0 3.2 1.4 3.2 3.2s-1.4 3.2-3.2 3.2-3.3-1.3-3.3-3.1 1.4-3.3 3.3-3.3z"></path></svg>'; // Replace with an actual icon if desired

              // Add search (geocoding) control to the map
              const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                placeholder: "Go to a location",
                bbox: nswBbox,
                marker: { color: "#F9AE2B" },
                countries: "AU",
              });

              // Listen to the 'results' event
              geocoder.on("results", function (event) {
                if (event.features && event.features.length > 3) {
                  // Limit the results to the top 3
                  event.features = event.features.slice(0, 3);
                }
              });

              this.container.appendChild(button);
              this.container.appendChild(geocoder.onAdd(map));
              // debugger
              geocoder.container.style.display = "none";
              geocoder.container.style.margin = 0;

              button.addEventListener("click", () => {
                if (geocoder.container.style.display === "none") {
                  geocoder.container.style.display = "block";
                  button.style.display = "none";
                  geocoder.container.focus();
                }
              });

              map.on("click", () => {
                if (geocoder.container.style.display === "block") {
                  geocoder.container.style.display = "none";
                  button.style.display = "block";
                }
              });

              return this.container;
            },

            onRemove: function () {
              this.container.parentNode.removeChild(this.container);
              this.map = undefined;
            },
          };

          const ResetViewControl = {
            onAdd: function (map) {
              this._map = map;
              this._btn = document.createElement("button");
              this._btn.type = "button";
              this._btn.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-reset"; // Assigning default classnames
              this._btn.title = "Reset view";
              this._btn.onclick = () => {
                map.flyTo({
                  center: isMobile
                    ? [147.31905, -34.47493703000571]
                    : [147.31905, -33.083640948961005], // Provide your initial center
                  zoom: isMobile ? 3.932423459616931 : 4.640339082781262, // Provide your initial zoom level
                });
              };
              this._btn.innerHTML = `<img alt='shape of NSW' width='22px' style='position:relative;top:2px;width:22px!important' src='${MEDIA_URL}/img/NSW-shape.svg'/>`; // You can also use an SVG or any other icon here

              this._container = document.createElement("div");
              this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
              this._container.appendChild(this._btn);

              return this._container;
            },

            onRemove: function () {
              this._container.parentNode.removeChild(this._container);
              this._map = undefined;
            },
          };
          map.addControl(ResetViewControl, "top-right"); // Adjust the position (e.g., 'top-right', 'bottom-left', etc.) as desired

          // Add zoom and rotation controls to the map.
          map.addControl(new mapboxgl.NavigationControl(), "top-right");

          map.addControl(
            RetractableGeocoderControl,
            isMobile ? "bottom-left" : "top-right"
          );

          setMapLoading(false);
        }
      });

      setMap(map);
    };

    if (!map && geoJsonData) initializeMap({ setMap, mapContainer });

    // return () => {
    //   if (map) {
    //     map.remove();
    //   }
    // };
  }, [map, geoJsonData]);

  useEffect(() => {
    function loadImage(name, src) {
      return new Promise((resolve, reject) => {
        map.loadImage(src, (error, image) => {
          if (error) {
            reject(error);
            return;
          }
          if (!map.hasImage(name)) {
            map.addImage(name, image);
          }
          resolve(name);
        });
      });
    }

    if (map) {
      const iconNames = new Set([
        ...Object.values(SCHOOL_LEVEL_ICON_NAMES),
        DEFAULT_ICON_NAME,
      ]);
      Promise.all(
        Array.from(iconNames, (iconName) =>
          loadImage(iconName, `${MEDIA_URL}/img/${iconName}.png`)
        )
      )
        .then(() => {
          // All icons have been loaded and added.
          //Now you can add layers or other tasks that depend on these images.
          setIconsLoading(false);
        })
        .catch((error) => {
          console.error("Error loading an image:", error);
        });
    }
  }, [map]);

  useEffect(() => {
    if (!iconsLoading && !styleLoading) {
      // Add geojson data as a new source to the map
      if (!map.getSource("messages")) {
        map.addSource("messages", {
          type: "geojson",
          data: geoJsonData,
        });
      }

      // Add a layer to use the symbols source with specific icons for each school level
      if (!map.getLayer("messages")) {
        map.addLayer({
          id: "messages",
          type: "symbol",
          source: "messages",
          layout: {
            "icon-image": [
              "match",
              ["get", "schoollevel"],
              ...Object.entries(SCHOOL_LEVEL_ICON_NAMES).flat(),
              DEFAULT_ICON_NAME, // default if no match
            ],
            // 'icon-image': 'diamond',
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              1,
              0.1, // At zoom level 1, icon size is 0.5
              10,
              0.28, // At zoom level 10, icon size is 1
              20,
              0.7, // At zoom level 20, icon size is 2
            ],

            "icon-allow-overlap": true,
            // Draw order follows this key ascending, so the higher value puts
            // the CEO and Deputy Premier gems above the submitted ones instead
            // of letting a dense cluster bury them.
            "symbol-sort-key": [
              "match",
              ["get", "schoollevel"],
              SPECIAL_SCHOOL_LEVELS,
              2,
              1,
            ],
          },
        });
      }

      // Highlight layer for selected message markers
      if (!map.getLayer("highlight-layer")) {
        map.addLayer({
          id: "highlight-layer",
          type: "symbol",
          source: "messages",
          layout: {
            "icon-image": "animatedDot",
          },
          filter: ["==", "id", ""], // Initialize with an impossible filter so no features are shown
        });
      }

      // });

      map.on("click", function (e) {
        // Unselect marker
        if (!e.features || e.features.length === 0) {
          map.setFilter("highlight-layer", ["==", "id", ""]); // Reset the filter
          infoContainer.current.innerHTML = introduction;
        }
      });

      map.on("click", "messages", function (e) {
        // Ensure that we actually clicked on a feature in our symbols layer
        if (e.features.length > 0) {
          const feature = pickClickedFeature(e.features, e.point, (coordinates) =>
            map.project(coordinates)
          );
          const featureId = feature.properties.id;
          const icon = getIcon(feature.properties.schoollevel);
          // const iconName = icon ? icon.split('img/')[1].replace('.png', '') : '';
          // A row can be safe/published/valid-school and still have no
          // message text (neither a preset gem nor a custom message).
          const messageWords = (feature.properties.message || "").split(" ");
          let message = "";
          // Wrap each word in a span with a random delay
          for (let word of messageWords) {
            let randomDelay = Math.random() * 2; // random number between 0 and 2
            message += `<span class=${styles.word} style="--delay:${randomDelay}">${word}</span> `;
          }

          // Create content for the popup from the feature's properties
          let content = `
                            <img id="${styles.gemIcon}" class="${styles.icon} ${
            styles.single
          }" src=${icon} alt='' role="presentation" />
                            <div class=${styles.messageContainer}>
                              <p class=${styles.message}>${message}</p>
                              <span class=${styles.sender}>${
            feature.properties.school ? feature.properties.school : ""
          }</span>
                            </div>
                          `;
          infoContainer.current.innerHTML = content;
          // Set the filter for the highlight-layer to only show the feature with the clicked id
          map.setFilter("highlight-layer", ["==", "id", featureId]);
        }
      });
    }
  }, [iconsLoading, styleLoading, geoJsonData, introduction, map]);

  return (
    <div className={styles.Map}>
      <div className={styles.mapContainer} ref={mapContainer}></div>
      <div
        className={styles.infoContainer}
        ref={infoContainer}
        tabIndex="0"
        dangerouslySetInnerHTML={{ __html: introduction }}
      />
      {loadError ? (
        <div className={styles.overlay}>
          <p className={styles.errorMessage} role="alert">
            Sorry — the map data couldn’t be loaded. Please refresh to try
            again.
          </p>
        </div>
      ) : (
        mapLoading && (
          <div className={styles.overlay}>
            <div className={styles.spinner}></div>
          </div>
        )
      )}
    </div>
  );
};

export default Map;
